import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { supabase } from '@/integrations/supabase/client';
import { DollarSign, Settings, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface QuickPriceEstimateProps {
  totalSquares: number;
  avgPitchRatio: string; // e.g. "6/12"
  onEstimateChange?: (data: {
    totalSquares: number;
    wastePercent: number;
    materialRate: number;
    laborRate: number;
    tearOffCost: number;
    dumpFee: number;
    overheadPercent: number;
    profitPercent: number;
    costSubtotal: number;
    overheadAmount: number;
    profitAmount: number;
    customerPrice: number;
  }) => void;
}

interface PricingSettings {
  roof_price_per_sq_asphalt: number;
  roof_price_per_sq_metal: number;
  roof_price_per_sq_flat: number;
  roof_tearoff_per_sq: number;
  roof_dump_fee_per_sq: number;
  roof_base_waste_percent: number;
  roof_steep_pitch_extra_percent: number;
  profit_margin_percent: number;
  overhead_percent: number;
}

export function QuickPriceEstimate({ totalSquares, avgPitchRatio, onEstimateChange }: QuickPriceEstimateProps) {
  const [settings, setSettings] = useState<PricingSettings | null>(null);
  const [roofMaterial, setRoofMaterial] = useState<'asphalt' | 'metal' | 'flat'>('asphalt');
  const [includeTearoff, setIncludeTearoff] = useState<'yes' | 'no'>('yes');
  const [loading, setLoading] = useState(true);
  const [wastePercent, setWastePercent] = useState(10);
  const [profitPercent, setProfitPercent] = useState(20);
  
  const { user, loading: authLoading } = useAuth();

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  useEffect(() => {
    if (!authLoading && user) {
      loadSettings();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [authLoading, user]);

  // Initialize sliders when settings load
  useEffect(() => {
    if (settings) {
      const pitchRise = parseInt(avgPitchRatio.split('/')[0]) || 0;
      const isSteepPitch = pitchRise >= 8;
      const calculatedWaste = settings.roof_base_waste_percent + (isSteepPitch ? settings.roof_steep_pitch_extra_percent : 0);
      setWastePercent(calculatedWaste);
      setProfitPercent(settings.profit_margin_percent);
    }
  }, [settings, avgPitchRatio]);

  // Calculate values using slider values
  const squaresWithWaste = totalSquares * (1 + wastePercent / 100);

  const materialRate = settings
    ? (roofMaterial === 'asphalt' ? settings.roof_price_per_sq_asphalt :
       roofMaterial === 'metal' ? settings.roof_price_per_sq_metal :
       settings.roof_price_per_sq_flat)
    : 0;

  const baseRoofPrice = squaresWithWaste * materialRate;
  const tearoffCost = (settings && includeTearoff === 'yes') ? squaresWithWaste * settings.roof_tearoff_per_sq : 0;
  const dumpCost = (settings && includeTearoff === 'yes') ? squaresWithWaste * settings.roof_dump_fee_per_sq : 0;
  const subtotalCost = baseRoofPrice + tearoffCost + dumpCost;
  
  const overheadCost = settings ? subtotalCost * (settings.overhead_percent / 100) : 0;
  const costWithOverhead = subtotalCost + overheadCost;
  const profitAmount = costWithOverhead * (profitPercent / 100);
  const finalCustomerPrice = costWithOverhead + profitAmount;

  // Notify parent of estimate data changes - ALWAYS called, but only executes if settings exist
  useEffect(() => {
    if (settings && onEstimateChange) {
      onEstimateChange({
        totalSquares: squaresWithWaste,
        wastePercent: wastePercent,
        materialRate: materialRate,
        laborRate: 0,
        tearOffCost: tearoffCost,
        dumpFee: dumpCost,
        overheadPercent: settings.overhead_percent,
        profitPercent: profitPercent,
        costSubtotal: subtotalCost,
        overheadAmount: overheadCost,
        profitAmount: profitAmount,
        customerPrice: finalCustomerPrice,
      });
    }
  }, [settings, roofMaterial, includeTearoff, totalSquares, avgPitchRatio, wastePercent, profitPercent, onEstimateChange]);

  const loadSettings = async () => {
    try {
      if (!user) {
        console.log('No user found for pricing settings');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('pricing_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading pricing settings:', error);
        throw error;
      }
      
      console.log('Loaded pricing settings:', data);
      setSettings(data);
    } catch (error) {
      console.error('Error loading pricing settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // NOW we can do early returns AFTER all hooks have been called
  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse text-muted-foreground">Loading pricing...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            Quick Price Estimate
          </CardTitle>
          <CardDescription>Instant ballpark pricing</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Set your rates to unlock instant ballpark pricing.</span>
              <Button asChild variant="outline" size="sm">
                <Link to="/settings?tab=pricing">
                  <Settings className="w-4 h-4 mr-2" />
                  Setup Rates
                </Link>
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-primary" />
          Quick Price Estimate (Ballpark)
        </CardTitle>
        <CardDescription>Based on your pricing rates</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Input Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Roof Material</Label>
            <Select value={roofMaterial} onValueChange={(v: any) => setRoofMaterial(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asphalt">Asphalt Shingles</SelectItem>
                <SelectItem value="metal">Metal Roof</SelectItem>
                <SelectItem value="flat">Flat Roof</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Include Tear-off & Dump?</Label>
            <Select value={includeTearoff} onValueChange={(v: any) => setIncludeTearoff(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Cost Breakdown */}
        <div className="space-y-3 border-t pt-4">
          <h4 className="font-semibold text-sm mb-3">Material & Labor Costs</h4>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Squares (no waste)</span>
            <span className="font-medium">{Math.ceil(totalSquares)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              Waste ({wastePercent.toFixed(0)}%)
            </span>
            <span className="font-medium">{Math.ceil(squaresWithWaste)} squares</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Material rate</span>
            <span className="font-medium">${materialRate.toFixed(0)}/sq</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Roof material</span>
            <span className="font-medium">${baseRoofPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          
          {includeTearoff === 'yes' && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tear-off</span>
                <span className="font-medium">${tearoffCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Dump fee</span>
                <span className="font-medium">${dumpCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </>
          )}
          
          <div className="flex justify-between text-sm font-medium pt-2 border-t">
            <span>Subtotal (Cost)</span>
            <span>${subtotalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        {/* Business Costs */}
        <div className="space-y-3 border-t pt-4">
          <h4 className="font-semibold text-sm mb-3">Business Costs & Profit</h4>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Overhead ({settings.overhead_percent}%)</span>
            <span className="font-medium">${overheadCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Cost + Overhead</span>
            <span className="font-medium">${costWithOverhead.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Profit Margin ({profitPercent.toFixed(0)}%)</span>
            <span className="font-medium text-success">${profitAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        {/* Adjustment Sliders */}
        <div className="space-y-4 border-t pt-4">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label>Waste Factor</Label>
              <span className="text-sm font-medium">{wastePercent.toFixed(0)}%</span>
            </div>
            <Slider
              value={[wastePercent]}
              onValueChange={(values) => setWastePercent(values[0])}
              min={5}
              max={25}
              step={1}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label>Profit Margin</Label>
              <span className="text-sm font-medium">{profitPercent.toFixed(0)}%</span>
            </div>
            <Slider
              value={[profitPercent]}
              onValueChange={(values) => setProfitPercent(values[0])}
              min={10}
              max={40}
              step={1}
              className="w-full"
            />
          </div>
        </div>

        {/* Customer Price */}
        <div className="border-t pt-4 bg-primary/5 -mx-6 px-6 pb-6 rounded-b-lg">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold">Customer Price</span>
            <span className="text-3xl font-bold text-primary">
              ${finalCustomerPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="mt-2 flex justify-between text-xs text-muted-foreground">
            <span>Your Profit:</span>
            <span className="font-medium text-success">${profitAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            This is a ballpark estimate. Actual pricing may vary based on site conditions, permits, and additional labor.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
